import { SocialLogin } from '@capgo/capacitor-social-login';
import { isNativeApp, getPlatformName, isIOSDevice } from '@/utils/platform';
import { SITE_URL } from '@/config/api';

/**
 * Native (Capacitor) social sign-in using @capgo/capacitor-social-login.
 * Only used inside the native app shell. On the web we keep the standard
 * @react-oauth/google and react-apple-login flows.
 */

// Web OAuth client id (also used as the server/webClientId for Google native verification).
const GOOGLE_WEB_CLIENT_ID =
  '113709107751-i3nl17s0clr60ntunl4b9f9l3i60u7fg.apps.googleusercontent.com';

// iOS OAuth client id. This is required for the native Google Sign-In on iOS.
const GOOGLE_IOS_CLIENT_ID =
  '113709107751-3qdnaduo6456uqu5cbchqetqhshfsb8k.apps.googleusercontent.com'; // REMPLACEZ PAR VOTRE VRAIE VALEUR

// Apple Service ID (must match the Laravel backend "aud" check).
const APPLE_CLIENT_ID = 'com.sportlogin.login';
const APPLE_NATIVE_BUNDLE_ID = 'com.sportlogin.app';

let initialized = false;

/**
 * Initializes the SocialLogin plugin. Can be called multiple times safely.
 * This should be called as early as possible to prevent popup blockers.
 */
export async function ensureSocialLoginInitialized() {
  if (initialized) return;
  await SocialLogin.initialize({
    google: {
      // On iOS you typically need the iOS client id too; webClientId is used to
      // request an idToken that the backend can verify.
      iOSClientId: GOOGLE_IOS_CLIENT_ID,
      webClientId: GOOGLE_WEB_CLIENT_ID,
      redirectUrl: `${SITE_URL}/google-auth-done`,
    },
    apple: {
      clientId: APPLE_CLIENT_ID,
      redirectUrl: `${SITE_URL}/apple-auth-done`,
    },
  });
  initialized = true;
}

/**
 * Native Google sign-in. Returns an idToken to send to /api/auth/google.
 */
export async function nativeGoogleSignIn(isNative: boolean): Promise<string> {
  if (!isNative) throw new Error('nativeGoogleSignIn called outside native app');
  await ensureSocialLoginInitialized();
  const res: any = await SocialLogin.login({
    provider: 'google',
    options: {
      // Requesting 'email' scope is crucial to ensure an idToken is returned.
    },
  });
  const idToken = res?.result?.idToken || res?.result?.accessToken?.token;
  if (!idToken) throw new Error('No idToken returned from native Google sign-in');
  return idToken;
}

/**
 * Native Apple sign-in. Returns an identityToken to send to /api/auth/apple.
 */
export async function nativeAppleSignIn(isNative: boolean): Promise<string> {
  if (!isNative) throw new Error('nativeAppleSignIn called outside native app');
  if (!isIOSDevice()) throw new Error('Apple sign-in is only available on iOS');
  await ensureSocialLoginInitialized();
  const res: any = await SocialLogin.login({
    provider: 'apple',
    options: {
      scopes: ['email', 'name'],
    },
  });
  const idToken = res?.result?.idToken;
  if (!idToken) throw new Error('No identityToken returned from native Apple sign-in');
  return idToken;
}
